"use client";

import Link from "next/link";
import { useState } from "react";
import { ArrowUpRight, DatabaseZap } from "lucide-react";
import type { PriceRow } from "@/lib/api";
import { formatDateTr } from "@/lib/date-format";
import { productHref } from "@/lib/product-links";
import ProductImage from "@/components/ui/ProductImage";
import { sortProductRows } from "./market-explorer-utils";

function money(value: number | string | null) {
  if (value == null || value === "") return "—";
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";
}

function priceText(row: PriceRow) {
  const min = Number(row.minPrice);
  const max = Number(row.maxPrice);
  if (min > 0 && max > 0) return `${money(row.minPrice)} – ${money(row.maxPrice)}`;
  if (Number(row.avgPrice) > 0) return money(row.avgPrice);
  return min > 0 ? money(row.minPrice) : max > 0 ? money(row.maxPrice) : "—";
}

export default function MarketProductTable({ rows, loading, marketName, sourceKind = "hal" }: { rows: PriceRow[]; loading: boolean; marketName: string; sourceKind?: "hal" | "borsa" | "aggregate" }) {
  const fishRows = rows.filter((row) => row.categorySlug?.startsWith("balik"));
  const produceRows = rows.filter((row) => /sebze|meyve|ithal/u.test(row.categorySlug ?? ""));
  const otherRows = rows.filter((row) => !fishRows.includes(row) && !produceRows.includes(row));
  const splitCategories = sourceKind === "hal" && fishRows.length > 0 && produceRows.length > 0;
  const [category, setCategory] = useState<"produce" | "fish" | "other">("produce");
  const categoryRows = splitCategories ? category === "fish" ? fishRows : category === "other" ? otherRows : produceRows : rows;
  const products = sortProductRows(categoryRows);
  const [expanded, setExpanded] = useState(false);
  const visibleProducts = expanded ? products : products.slice(0, 6);

  if (loading && products.length === 0) {
    return <div className="divide-y divide-(--color-border-soft) border-y border-(--color-border)" aria-live="polite">
      {Array.from({ length: 6 }, (_, index) => <div key={index} className="h-[74px] animate-pulse bg-(--color-bg-alt)" />)}
    </div>;
  }

  if (products.length === 0) {
    return <div className="border-y border-(--color-border) py-16 text-center">
      <DatabaseZap className="mx-auto size-7 text-(--color-muted)" aria-hidden />
      <h2 className="type-h2 mt-4">{sourceKind === "aggregate" ? "Bu kaynak" : sourceKind === "borsa" ? "Bu borsa" : "Bu hal"} için yayımlanmış ürün yok</h2>
      <p className="mt-2 text-sm text-(--color-muted)">Kaynak yeni bülten yayımladığında fiyatlar burada otomatik görünür.</p>
    </div>;
  }

  return <div className="border-y border-(--color-border)">
    {splitCategories ? <div className="flex flex-wrap gap-2 border-b border-(--color-border-soft) py-3" aria-label="Ürün grubu">
      {([
        ["produce", "Sebze ve meyve", produceRows.length],
        ["fish", "Balık ve su ürünleri", fishRows.length],
        ...(otherRows.length ? [["other", "Diğer ürünler", otherRows.length]] : []),
      ] as Array<["produce" | "fish" | "other", string, number]>).map(([key, label, count]) => <button key={key} type="button" aria-pressed={category === key} onClick={() => { setCategory(key); setExpanded(false); }} className={`min-h-10 border px-3 py-2 text-label font-semibold ${category === key ? "border-(--color-foreground) bg-(--color-foreground) text-(--color-surface)" : "border-(--color-border) text-(--color-foreground) hover:border-(--color-foreground)"}`}>{label}<span className="ml-1.5 font-normal opacity-75">({count})</span></button>)}
    </div> : null}
    <div className="hidden grid-cols-[minmax(240px,1.35fr)_minmax(170px,.8fr)_90px_140px_32px] gap-5 border-b border-(--color-border) py-3 text-eyebrow font-semibold uppercase tracking-[0.08em] text-(--color-muted) md:grid">
      <span>Ürün</span><span>Fiyat / aralık (TL)</span><span>Birim</span><span>Kayıt tarihi</span><span />
    </div>
    <div className="divide-y divide-(--color-border-soft)" aria-busy={loading} aria-label={`${marketName} ${splitCategories ? category === "fish" ? "balık ve su ürünleri" : category === "other" ? "diğer ürünler" : "sebze ve meyve" : "ürün"} fiyatları`}>
      {visibleProducts.map((row, index) => <Link key={`${row.marketSlug}-${row.productSlug}`} href={productHref(row)} prefetch={false} className="group grid min-h-[72px] grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-3 transition-colors hover:bg-(--color-bg-alt) md:grid-cols-[minmax(240px,1.35fr)_minmax(170px,.8fr)_90px_140px_32px] md:gap-5 md:px-2">
        <span className="flex min-w-0 items-center gap-3">
          <ProductImage slug={row.productSlug} canonicalSlug={row.canonicalProduct} name={row.productName} categorySlug={row.categorySlug} imageUrl={row.imageUrl} size={48} priority={index < 6} className="!rounded-none !border-0 !shadow-none" />
          <strong className="truncate text-body text-(--color-foreground)">{row.productName}</strong>
        </span>
        <span className="text-right md:text-left">
          <strong className="block font-(family-name:--font-mono) text-label text-(--color-foreground)">{priceText(row)}</strong>
          <small className="mt-1 block text-eyebrow text-(--color-muted) md:hidden">{row.unit} · {formatDateTr(row.recordedDate)}</small>
        </span>
        <span className="hidden text-data text-(--color-muted) md:block">{row.unit}</span>
        <time className="hidden text-data text-(--color-muted) md:block" dateTime={row.recordedDate.slice(0, 10)}>{formatDateTr(row.recordedDate)}</time>
        <ArrowUpRight className="hidden size-4 text-(--color-muted) transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 md:block" aria-hidden />
      </Link>)}
    </div>
    {products.length > 6 ? <button type="button" onClick={() => setExpanded((value) => !value)} className="min-h-12 w-full border-t border-(--color-border-soft) text-data font-bold text-(--color-foreground) transition-colors hover:bg-(--color-bg-alt)">{expanded ? "Listeyi kısalt" : `Tüm ${products.length} ürünü göster`}</button> : null}
  </div>;
}
